import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Flex, Menu, Popover, Radio, Spin } from 'antd'
import { MenuItemGroupType } from 'antd/es/menu/interface'
import { useTheme } from 'antd-style'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useLoginServiceUserTokenService,
  useUserServiceGetUserProfileEndpoint,
  useUserServiceGetUserRolesListEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_ID_KEY,
  USER_INSTITUTION_KEY,
  USER_ROLE_KEY
} from 'src/app-constants'
import { Text } from 'src/components'
import { useSessionContext } from 'src/contexts'
import { useLocalStorage } from 'src/hooks'
import { paths } from 'src/paths.ts'
import { parseToken } from 'src/utils'

import { useStyles as useCardStyles } from '../ProfileCard/styles'

import { useStyles } from './styles'

type Data = {
  name: string
  id: string
}

type Role = {
  role: Data
  institutions: Data[]
}

type RoleWithInstitution = Data & {
  institutionId: string
}

type GroupedRoles = {
  [institutionName: string]: RoleWithInstitution[]
}

const groupRolesByInstitution = (roles: Role[]): GroupedRoles => {
  return roles.reduce((grouped, role) => {
    role.institutions.forEach((institution) => {
      if (!grouped[institution?.name]) {
        grouped[institution?.name] = []
      }

      if (!grouped[institution?.name].some((r) => r.name === role.role.name)) {
        grouped[institution.name].push({
          name: role.role?.name,
          id: role.role?.id,
          institutionId: institution?.id
        })
      }
    })
    return grouped
  }, {} as GroupedRoles)
}

const REFRESH_ERROR_MESSAGE = 'The refresh token is not valid!'

export const RoleChanger = () => {
  const [selectedRole, setSelectedRole] = useState<string>()
  const [isOpen, setIsOpen] = useState(false)

  const { styles } = useStyles()
  const palette = useTheme()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading } = useUserServiceGetUserProfileEndpoint()
  const { data: userRoles } = useUserServiceGetUserRolesListEndpoint()

  const { onLogout } = useSessionContext()

  const { mutate } = useLoginServiceUserTokenService({
    onSuccess: async (data) => {
      setSelectedRole(`${data?.legalEntityId}/${data?.roleId}`)
      setUserRoleKey(String(data?.roleId))
      setInstitutionKey(String(data?.legalEntityId))
      setRefreshToken(String(data?.refreshToken))
      setAuthToken(String(data?.accessToken))
      queryClient.invalidateQueries()
      navigate(paths.root)
      navigate(0)
    },
    onError: (err: ApiError) => {
      if (err.body.errors.generalErrors[0] === REFRESH_ERROR_MESSAGE) {
        onLogout()
      }
    }
  })

  const { styles: cardStyles } = useCardStyles()

  const { setValue: setUserRoleKey } = useLocalStorage(USER_ROLE_KEY, '')
  const { setValue: setInstitutionKey } = useLocalStorage(
    USER_INSTITUTION_KEY,
    ''
  )
  const { setValue: setRefreshToken } = useLocalStorage(REFRESH_TOKEN_KEY, '')
  const { setValue: setAuthToken } = useLocalStorage(ACCESS_TOKEN_KEY, '')

  const onOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen)
  }

  const onRoleChange = (roleId: string, institutionId?: string) => {
    if (selectedRole === `${institutionId}/${roleId}`) {
      return
    }

    mutate({
      requestBody: {
        refreshToken: parseToken(localStorage.getItem(REFRESH_TOKEN_KEY)),
        userId: parseToken(localStorage.getItem(USER_ID_KEY)),
        roleId: String(roleId),
        legalEntityId: String(institutionId)
      }
    })
  }

  const currentUserRole = useMemo(
    () => userRoles?.data?.find((role) => role.id === data?.currentRoleId),
    [userRoles, data]
  )

  const currentOrganization = useMemo(
    () =>
      currentUserRole?.legalEntities?.find(
        (inst) => inst.id === data?.currentLegalEntityId
      ),
    [currentUserRole, data]
  )

  const allRolesByInstitution = useMemo(
    () =>
      userRoles?.data?.map((role) => ({
        role: {
          name: role?.name || '',
          id: role?.id || ''
        },
        institutions:
          role?.legalEntities?.map((inst) => ({
            name: inst?.name || '',
            id: inst?.id || ''
          })) || []
      })) || [],
    [userRoles]
  )

  const groupedRoles = useMemo(
    () => groupRolesByInstitution(allRolesByInstitution),
    [allRolesByInstitution]
  )

  const menuItems: MenuItemGroupType[] = Object.entries(groupedRoles).map(
    ([institutionName, roles]) => ({
      type: 'group',
      label: <Text color={palette.black.black1}>{institutionName}</Text>,
      children: roles.map((role) => {
        const formattedRole = `${role.institutionId}/${role.id}`

        const isCurrentRole = selectedRole === formattedRole

        return {
          key: formattedRole,
          label: (
            <Flex
              justify="space-between"
              className="full-width"
              align="center"
              gap={8}
            >
              <Text size="md" color={palette.grey.grey1}>
                {role.name}
              </Text>
              <Radio checked={isCurrentRole} />
            </Flex>
          ),
          onClick: () => onRoleChange(role?.id, role?.institutionId),
          selected: isCurrentRole
        }
      })
    })
  )

  useEffect(() => {
    if (data) {
      setSelectedRole(`${data?.currentLegalEntityId}/${data?.currentRoleId}`)
      setUserRoleKey(String(data?.currentRoleId))
      setInstitutionKey(String(data?.currentLegalEntityId))
      queryClient.invalidateQueries()
    }
  }, [data])

  return (
    <Spin spinning={isLoading} size="small">
      <Popover
        content={
          <Menu mode="inline" items={menuItems} className={styles.menu} />
        }
        onOpenChange={onOpenChange}
        open={isOpen}
        arrow={false}
        overlayClassName={styles.popover}
        placement="bottomRight"
      >
        <Flex gap={16} align="center" className={cardStyles.profileCard}>
          <Flex vertical>
            <Text weight={600} color={palette.black.black1}>
              {currentUserRole?.name}
            </Text>
            <Text color={palette.grey.grey1}>{currentOrganization?.name}</Text>
          </Flex>

          <div className={cardStyles.arrow}>
            {isOpen ? <CaretUpOutlined /> : <CaretDownOutlined />}
          </div>
        </Flex>
      </Popover>
    </Spin>
  )
}
